import { Theme } from '@mui/material/styles';
import { semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import { gradientBorderBackground } from '@/theme/utils';

export const getSignupStyles = (theme: Theme) => ({
  formContent: {
    width: '100%',
    maxWidth: '480px',
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xs: '1rem', sm: '1.5rem' },
    mt: 1,
    mb: 1,
  },

  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
  },

  title: {
    fontSize: { xs: '1.5rem', sm: '1.625rem' },
    lineHeight: 1.25,
    fontWeight: 700,
    color: theme.tokens.colors.textPrimary,
    textAlign: 'left',
    m: 0,
  },

  subtitle: {
    fontSize: '0.95rem',
    lineHeight: 1.45,
    color: theme.tokens.colors.textTertiary,
    textAlign: 'left',
  },

  // The picker is a MUI ``size="small"`` Select shared with Settings; scope the
  // 48px rhythm of the surrounding fields to this form rather than resizing it
  // everywhere, so the language bar stops stepping taller than its neighbours.
  languageField: {
    width: '100%',
    '& .MuiOutlinedInput-root': { height: '48px' },
  },

  termsPaper: {
    p: 1.5, // Condensed space, no logo
    mb: 1.5,
    mt: 1,
    borderRadius: BORDER_RADIUS,
    background: theme.tokens.colors.surfaceWarm,
    width: '100%',
    maxWidth: '100%',
    border: `1px solid ${theme.tokens.colors.borderLight}`,
    mx: 'auto',
  },

  communicationContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0, // No logo, no gap needed
    width: '100%',
  },

  termsContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0, // No logo, no gap needed
  },

  addressSection: {
    width: '100%',
    marginBottom: '0.375rem', // Reduced by 50% from 0.75rem
  },

  fieldLabel: {
    mb: 0.75,
    color: theme.tokens.colors.textBody,
    fontSize: '0.9rem',
    fontWeight: 500,
    textAlign: 'left',
    '@media (max-width: 768px)': {
      mb: 0.5,
    },
  },

  // aria-hidden asterisk uses inherit color; AT gets required state from the input's native
  // attribute.
  requiredMarker: {
    color: 'inherit',
  },

  communicationHeader: {
    fontWeight: 600,
    fontSize: '1.05rem',
    color: theme.tokens.colors.textBody,
    textAlign: 'left',
    marginBottom: '0.5rem',
  },

  privacyNote: {
    fontSize: '0.95rem',
    color: theme.tokens.colors.textBody,
    textAlign: 'left',
    lineHeight: 1.5,
    fontStyle: 'italic',
  },

  consentSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    pr: 0.5,
  },

  consentCheckbox: {
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginTop: '-4px',
    },
    '& .Mui-checked svg': {
      color: theme.tokens.colors.textPrimary,
    },
  },

  consentLabel: {
    fontSize: '0.9rem',
    color: theme.tokens.colors.textBody,
    lineHeight: 1.4,
    textAlign: 'left',
    '& a': {
      color: theme.tokens.colors.link,
      textDecoration: 'underline',
    },
  },

  phoneSection: {
    width: '100%',
    marginBottom: '1rem',
  },

  phoneField: {
    '& .MuiOutlinedInput-root': {
      height: '48px',
      borderRadius: BORDER_RADIUS,
      backgroundColor: theme.tokens.colors.background,
      '& fieldset': { borderColor: theme.tokens.colors.inputBorder },
      '&:hover fieldset': { borderColor: theme.tokens.colors.inputBorderHover },
      '&.Mui-focused fieldset': { borderColor: theme.tokens.colors.inputBorderFocus },
    },
  },

  termsHeader: {
    fontSize: '1.05rem',
    color: theme.tokens.colors.textBody,
    textAlign: 'left',
  },

  termsList: {
    width: '100%',
    listStyleType: 'disc',
    pb: 0,
  },

  termsItem: {
    display: 'list-item',
    fontSize: '0.9rem',
    color: theme.tokens.colors.textBody,
    lineHeight: 1.5,
    textAlign: 'left',
    '& a': {
      color: theme.tokens.colors.link,
      textDecoration: 'underline',
    },
  },

  signUpButton: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: BORDER_RADIUS,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    height: '48px',
    border: '2px solid transparent',
    background: gradientBorderBackground(
      theme.tokens.colors.background,
      semanticGradients.borderGradient
    ),
    color: theme.tokens.colors.textBody,
    '&:hover': {
      background: gradientBorderBackground(
        theme.tokens.colors.surfaceSubtle,
        semanticGradients.borderGradient
      ),
    },
    '&.Mui-disabled': {
      color: theme.tokens.colors.disabled,
      background: gradientBorderBackground(
        theme.tokens.colors.surfaceSubtle,
        semanticGradients.borderGradientDisabled
      ),
    },
  },

  goBackButton: {
    color: theme.tokens.colors.textTertiary,
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '0.9rem',
    alignSelf: 'center',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.tokens.colors.textBody,
    },
  },
});
